import { Component, computed, inject, input, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { marked } from 'marked';
import { MeoTurn } from '@app/_interfaces/meo.interface';
import { SettingsService } from '@app/_services/settings.service';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { log } from '@app/_services/logger';

@Component({
  selector: 'app-meo-message',
  standalone: true,
  imports: [CommonModule, SvgComponent],
  templateUrl: './meo-message.component.html',
  styleUrl: './meo-message.component.scss',
})
export class MeoMessageComponent {
  readonly ss = inject(SettingsService);
  private readonly storage = inject(Storage);
  turn = input.required<MeoTurn>();

  /** Markdown-rendered HTML for assistant messages. */
  parsedText = computed(() => {
    const text = this.turn().text;
    if (!text) return '';
    return marked.parse(text, { breaks: true, async: false }) as string;
  });


  /** Human-readable attachment metadata line (e.g., "Image · 245 KB"). */
  attachmentInfo = computed(() => {
    const att = this.turn().attachmentMeta;
    if (!att) return '';
    const parts: string[] = [];
    const mime = att.mimeType;
    if (mime?.startsWith('image/')) parts.push('Image');
    else if (mime === 'application/pdf') parts.push('PDF');
    else if (mime?.startsWith('audio/')) parts.push('Audio');
    else if (mime) parts.push('File');
    if (att.sizeBytes) {
      const b = att.sizeBytes;
      if (b < 1024) parts.push(`${b} B`);
      else if (b < 1024 * 1024) parts.push(`${Math.round(b / 1024)} KB`);
      else parts.push(`${(b / (1024 * 1024)).toFixed(1)} MB`);
    }
    return parts.join(' · ');
  });

  /** Resolved thumbnail URL (from Storage or ephemeral data URL). */
  thumbnailUrl = signal<string | null>(null);

  /** Download URL cache to avoid redundant fetches. */
  private static urlCache = new Map<string, string>();

  constructor() {
    effect(() => {
      const att = this.turn().attachmentMeta;
      if (!att) return;

      // Priority: thumbnailStoragePath > thumbnailDataUrl
      if (att.thumbnailStoragePath) {
        const cached = MeoMessageComponent.urlCache.get(att.thumbnailStoragePath);
        if (cached) {
          this.thumbnailUrl.set(cached);
        } else {
          this.loadThumbnailFromStorage(att.thumbnailStoragePath);
        }
      } else if (att.thumbnailDataUrl) {
        this.thumbnailUrl.set(att.thumbnailDataUrl);
      }
    });
  }

  private async loadThumbnailFromStorage(path: string): Promise<void> {
    try {
      const url = await getDownloadURL(ref(this.storage, path));
      MeoMessageComponent.urlCache.set(path, url);
      this.thumbnailUrl.set(url);
    } catch {
      // Fall back to thumbnailDataUrl if Storage fetch fails
      const att = this.turn().attachmentMeta;
      if (att?.thumbnailDataUrl) {
        this.thumbnailUrl.set(att.thumbnailDataUrl);
      }
    }
  }

  async openAttachment(): Promise<void> {
    const att = this.turn().attachmentMeta;
    if (!att?.storagePath) return;
    try {
      const url = await getDownloadURL(ref(this.storage, att.storagePath));
      window.open(url, '_blank');
    } catch (err) {
      log.error('MeoMessage', 'Failed to open attachment', err as Error);
    }
  }
}
